import { CommonDialogs } from "@mcleod/common";
import {
    BlurEvent, Component, DataSourceMode, Label, LookupModelSelectionEvent, Panel, Textbox
} from "@mcleod/components";
import { ListLayoutLauncher } from "@mcleod/components/src/page/ListLayoutLauncher";
import { Api, getThemeColor, HorizontalAlignment, Navigation, StringUtil } from "@mcleod/core";

export interface DuplicateIdProps {
    tableName: string,
    screenName: string,
    navigateToUrl: string,
    codeGenerationIsActive?: boolean,
    altFieldNameForDupCheck?: string,
    primaryKeyFieldNameForAlt?: string,
    compositeKeyValues?: Record<string, string>
    afterValidate?: (textbox: Textbox, props: DuplicateIdProps) => void
    displayDialogOnSave?: boolean;
    validationType?: "onSelect" | "onBlur",
    listLayout?: boolean
}

const PANEL_PROPS = {
    wrap: true,
    align: HorizontalAlignment.CENTER,
    fillRow: true,
    fillHeight: true
};

const LABEL_PROPS = {
    color: "default",
    fontSize: "large",
    rowBreak: false,
    paddingLeft: 1,
    paddingRight: 1,
    marginLeft: 0,
    marginRight: 0
};

const DIALOG_PROPS = {
    title: "Duplicate Code",
    xVisible: false,
    titleProps: {
        color: "default.reverse"
    },
    panelTitleProps: {
        paddingTop: 10,
        paddingBottom: 10,
        backgroundColor: "primary.darker"
    },
    okProps: {
        maxWidth: 100,
        backgroundColor: getThemeColor("primary"),
        color: getThemeColor("primary.reverse")
    }
};

export class DuplicateId {
    public static configureIdTextbox(textbox: Textbox, props: DuplicateIdProps) {
        if (props.validationType == "onSelect") {
            this.setupValidationOnSelect(textbox, props);
        } else {
            this.setupValidateOnBlur(textbox, props);
        }
        this.setupDataSourceBeforePost(textbox, props);
    }

    static setupValidateOnBlur(textbox: Textbox, props: DuplicateIdProps) {
        textbox.addBlurListener((event: BlurEvent) => {
            DuplicateId.validate(textbox, props);
        });
    }

    static setupValidationOnSelect(textbox: Textbox, props: DuplicateIdProps) {
        textbox.addLookupModelSelectionListener(async (event: LookupModelSelectionEvent) => {
            DuplicateId.validate(textbox, props);
        });
    }

    static async validate(tb: Textbox, props: DuplicateIdProps): Promise<boolean> {
        tb.dataSource.setComponentsBusy(true);
        if (tb.dataSource.mode == DataSourceMode.ADD) {
            const idToValidate = tb.getDataValue();
            if (StringUtil.isEmptyString(idToValidate)) {
                tb.validationWarning = "Required";
            } else {
                const response = await this.checkManualCode(props.tableName, idToValidate, props.altFieldNameForDupCheck, props.primaryKeyFieldNameForAlt, props.compositeKeyValues);
                tb.validationWarning = (response.data[0]?.is_duplicate)
                    ? this.getDuplicateCodeValidationWarning(idToValidate, tb.hasLookupModel() ? tb.text : null)
                    : null;
                if (tb.validationWarning && props.afterValidate) {
                    props.afterValidate(tb, props);
                }
            }
            if (tb.validationWarning) {
                tb.showAdhocTooltip(null, { timeout: 5000 });
            }
        }
        tb.dataSource.setComponentsBusy(false);
        return tb.validationWarning == null;
    }

    static setupDataSourceBeforePost(textbox: Textbox, props: DuplicateIdProps) {
        if (textbox.dataSource == null) return;
        const originalBeforePost = textbox.dataSource.doBeforeDataSourcePost;
        textbox.dataSource.doBeforeDataSourcePost = async () => {
            if (originalBeforePost) {
                const originalResult = await originalBeforePost();
                if (!originalResult) {
                    return false;
                }
            }
            return DuplicateId.validateBeforePost(textbox, props);
        };
    }

    public static async validateBeforePost(textbox: Textbox, props: DuplicateIdProps): Promise<boolean> {
        let result = true;
        if (textbox.dataSource.mode == DataSourceMode.ADD) {
            result = await DuplicateId.isValidCode(textbox, props);
            if (!result) {
                textbox.validationWarning = this.getDuplicateCodeValidationWarning(textbox.getDataValue(), textbox.hasLookupModel() ? textbox.text : null);
                textbox.clear();
            }
        }
        return result;
    }

    public static async isValidCode(textbox: Textbox, props: DuplicateIdProps): Promise<boolean> {
        let response: any;
        const id = textbox.getDataValue();
        if (props.codeGenerationIsActive && StringUtil.isEmptyString(id)) {
            const data = textbox.dataSource.activeRow?.data;
            response = await this.checkAutogen(props.tableName, data);
            if (response.data?.[0]?.code != null) {
                textbox.text = response.data[0].code;
            }
        } else if (!StringUtil.isEmptyString(id)) {
            response = await this.checkManualCode(props.tableName, id, props.altFieldNameForDupCheck, props.primaryKeyFieldNameForAlt, props.compositeKeyValues);
        }

        if (response == null) return textbox.validateSimple();

        if (response.data?.[0]?.is_duplicate) {
            const content = this.getDuplicateCodeContentPanel(
                textbox.getDataValue(), 
                props.screenName, 
                props.navigateToUrl, 
                response.data?.[0]?.pk_value, 
                textbox.hasLookupModel() ? textbox.text : null,
                props.listLayout ?? false
            );
            if (props.displayDialogOnSave !== false) {
                CommonDialogs.showDialog(content, DIALOG_PROPS).finally(() => {
                    textbox.enabledDuringAdd = true;
                    textbox.focus();
                });
            }
            return false;
        } else {
            return true;
        }
    }

    static async checkAutogen(tableName: string, data: any): Promise<any> {
        return await Api.post("lme/dispatch/check-manual-autogen",
            {
                "table_name": tableName,
                "table_row": data
            });
    }

    static async checkManualCode(table: string, id: string, altFieldNameForDupCheck?: string, primaryKeyFieldNameForAlt?: string, compositeKeyValues?: Record<string, string>): Promise<any> {
        return Api.post("lme/dispatch/check-duplicate-code",
            {
                "id": id,
                "table": table,
                "alt_field_name_for_dup_check": altFieldNameForDupCheck,
                "pk_field_name": primaryKeyFieldNameForAlt,
                "composite_key_values": compositeKeyValues
            });
    }

    public static getDuplicateCodeValidationWarning(id: string, descr?: string) {
        if (descr) {
            return `[ ${descr} ] is a duplicate of an existing code. Please enter a new one.`;
        }
        return `The code [ ${id} ] is a duplicate of an existing code. Please enter a new one.`;
    }

    public static getDuplicateCodeContentPanel(id: string, type: string, url: string, pkValue: string, descr?: string, listLayout?: boolean): Panel {
        const keyValue = pkValue ? pkValue : id;
        const linkOnClick = () =>{
            if (listLayout) {
                ListLayoutLauncher.open({
                    listLayout: url,
                    keyValues: [keyValue]
                });
            } else {
                Navigation.navigateTo(url + keyValue, { newTab: true });
            }
        }
        let components: Component[] = [];
        if (!StringUtil.isEmptyString(id)) {
            const panelTop = new Panel({ fillRow: true, align: HorizontalAlignment.CENTER, wrap: false });
            panelTop.add(
                new Label({ ...LABEL_PROPS, text: (descr ? "" : "The code ") }),
                new Label({
                    ...LABEL_PROPS,
                    fontSize: "xlarge",
                    fontBold: true,
                    color: "primary",
                    text: `[ ${descr ?? id} ]`,
                    tooltip: "Click to go to " + type + " screen",
                    onClick: () => linkOnClick()
                }),
                new Label({ ...LABEL_PROPS, rowBreak: true, text: " is a duplicate of an existing code." })
            );
            components = [
                panelTop,
                new Label({
                    ...LABEL_PROPS,
                    marginTop: 10,
                    fillRow: true,
                    align: HorizontalAlignment.CENTER,
                    text: "Please enter a new one."
                })
            ];
        }
        return new Panel({ ...PANEL_PROPS, components: components });
    }
}
